import { ChevronLeft, ChevronRight } from '@fountain/fountain-ui-components';
import { AppContext } from 'contexts/v2/app-context';
import React, { useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Skeleton } from 'components/ui/skeleton';

import { JobsList } from '../jobs-list/index';

export function PaginationSkeleton() {
  return (
    <div
      className="mt-3 flex items-center justify-center"
      data-testid="pagination-skeleton"
    >
      <Skeleton className="h-6 w-[200px]" />
    </div>
  );
}

export interface PaginationProps {
  currentPage: number;
  lastPage: number;
  setPage: (value: number) => void;
  jobsCount: number;
  perPage: number;
}

export function Pagination({
  currentPage,
  lastPage,
  setPage,
  jobsCount,
  perPage,
}: PaginationProps) {
  const {
    isMobile,
    isFetchingJobs,
    setIsFetchingJobs,
    updateUrlWithParams,
    accountSlug,
    brandId,
  } = useContext(AppContext);

  const disableNextPage = useMemo(
    () => currentPage === lastPage || !currentPage || lastPage === 0,
    [currentPage, lastPage],
  );

  const startResult = (currentPage - 1) * perPage + 1;
  const endResult = Math.min(currentPage * perPage, jobsCount);

  const shouldShowPagination = jobsCount > perPage;

  if (isMobile && isFetchingJobs && currentPage > 1) {
    return (
      <>
        <JobsList.Skeleton />
        <PaginationSkeleton />
      </>
    );
  }

  const handlePreviousPage = () => {
    const newPage = currentPage - 1;
    setIsFetchingJobs(true);
    setPage(newPage);
    if (accountSlug) {
      updateUrlWithParams(accountSlug, brandId, 'page', newPage.toString());
    }
  };

  const handleNextPage = () => {
    const newPage = currentPage + 1;
    setIsFetchingJobs(true);
    setPage(newPage);
    if (accountSlug) {
      updateUrlWithParams(accountSlug, brandId, 'page', newPage.toString());
    }
  };

  // Don't show pagination if there are 10 or fewer results
  if (!shouldShowPagination) {
    return null;
  }

  return (
    <div
      className={`text-14 font-400 mt-3 flex items-center justify-center gap-2 leading-4 ${
        disableNextPage && isMobile ? 'hidden' : ''
      }`}
      data-testid="pagination"
    >
      {isMobile ? (
        <button
          type="button"
          onClick={handleNextPage}
          className="font-[450] text-blue-400 underline hover:text-blue-600"
          data-testid="pagination-mobile-next"
        >
          <FormattedMessage
            defaultMessage="See more ({count})"
            description="Button to load more jobs on mobile pagination"
            values={{ count: jobsCount - currentPage * perPage }}
          />
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || !currentPage}
            className="flex items-center justify-center rounded border border-gray-300 p-1 text-indigo-600 hover:bg-gray-50 disabled:border-0 disabled:bg-gray-100 disabled:text-gray-400"
            data-testid="pagination-prev"
          >
            <ChevronLeft fontSize="small" />
          </button>
          <p>
            <FormattedMessage
              defaultMessage="{startResult} - {endResult} of {totalCount}"
              description="Range of results being displayed"
              values={{
                startResult,
                endResult,
                totalCount: jobsCount,
              }}
            />
          </p>
          <button
            type="button"
            onClick={handleNextPage}
            disabled={disableNextPage}
            className="flex items-center justify-center rounded border border-gray-300 p-1 text-indigo-600 hover:bg-gray-50 disabled:border-0 disabled:bg-gray-100 disabled:text-gray-400"
            data-testid="pagination-next"
          >
            <ChevronRight fontSize="small" />
          </button>
        </>
      )}
    </div>
  );
}
